/**
 * Generate Proposal — the operator's renewal editor. v2.04.
 *
 * Pulse does not raise invoices; FinOps does. This produces a customer
 * PROPOSAL, and nothing here ever sends one.
 *
 * The design rule that shapes this whole component: an annual increase is
 * a commercial decision, so the operator has to be able to try 3%, look at
 * what the customer would actually receive, try 5%, look again — and only
 * then commit. Every figure below is previewed by the SERVER without
 * writing (`?uplift_pct=` / `?mode=` on the preview endpoint), so what is
 * on screen is what would be saved, computed by the same helpers that
 * would persist it. Typing in the percentage box writes nothing.
 *
 * Persistence happens on exactly one click: Save draft.
 *
 * If a draft already exists for the upcoming period this opens THAT draft
 * rather than trying to create a second one — a renewal period has one
 * proposal, and the operator edits it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { money } from "./BillingSpecificationPanels";

const MODES = [
  ["detailed", "Detailed breakdown",
   "Every recurring line and its renewed price."],
  ["summary", "Summary only",
   "One renewal fee plus what's included. No line prices, no prior-year total, no uplift shown."],
];

export function GenerateProposalDialog({
  open, onOpenChange, clientId, currency, currentArr, onPrepared,
}) {
  const [loading, setLoading] = useState(true);
  const [specId, setSpecId] = useState(null);      // existing draft, if any
  const [upliftText, setUpliftText] = useState("0.00");
  const [mode, setMode] = useState("detailed");
  const [savedUplift, setSavedUplift] = useState(0);
  const [savedMode, setSavedMode] = useState("detailed");
  const [preview, setPreview] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounce = useRef(null);

  const pct = Number(upliftText);
  const pctValid = upliftText.trim() !== "" && Number.isFinite(pct) && pct >= -100;
  const base = Number(currentArr) || 0;
  const dirty = specId
    ? (pctValid && Math.abs(pct - savedUplift) > 0.0001) || mode !== savedMode
    : true;

  // ── Open: find the draft for the upcoming period, if there is one ──
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setPreview(null); setProposal(null);
    (async () => {
      try {
        const up = await api.get(`/renewals/clients/${clientId}/upcoming`);
        const draft = (up.data?.open_renewals || []).find(
          (d) => d.status === "draft" && !d.sent_to_client_at);
        if (cancelled) return;
        if (draft) {
          const r = await api.get(`/renewals/${draft.id}`);
          if (cancelled) return;
          const m = r.data?.presentation_mode || "detailed";
          const u = Number(r.data?.renewal?.uplift_pct ?? 0);
          setSpecId(draft.id);
          setUpliftText(u.toFixed(2));
          setMode(m); setSavedUplift(u); setSavedMode(m);
          setProposal(r.data?.proposal || null);
        } else {
          setSpecId(null); setUpliftText("0.00");
          setMode("detailed"); setSavedUplift(0); setSavedMode("detailed");
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load the renewal");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  // ── Live, NON-PERSISTING preview ──────────────────────────────────
  const refreshPreview = useCallback(async (nextPct, nextMode) => {
    if (!specId || !Number.isFinite(nextPct)) return;
    try {
      const r = await api.get(
        `/renewals/${specId}/preview?mode=${nextMode}&uplift_pct=${nextPct}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh the preview");
    }
  }, [specId]);

  useEffect(() => {
    if (!open || loading || !specId || !pctValid) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => refreshPreview(pct, mode), 350);
    return () => clearTimeout(debounce.current);
  }, [open, loading, specId, pct, pctValid, mode, refreshPreview]);

  const internal = preview?.internal;
  const cust = preview?.customer;
  // Server figure while previewing an existing draft; a client-side
  // indication only before the draft exists (nothing to preview yet).
  const proposedVal = internal ? internal.proposed_recurring_annual_value
    : (pctValid ? Math.round(base * (1 + pct / 100) * 100) / 100 : null);
  const increaseVal = proposedVal == null ? null
    : Math.round((proposedVal - base) * 100) / 100;

  // ── Save — the ONLY write ─────────────────────────────────────────
  const save = async () => {
    if (!pctValid) { toast.error("Enter a valid annual increase percentage."); return; }
    setBusy(true);
    try {
      let id = specId;
      if (!id) {
        const r = await api.post(`/renewals/clients/${clientId}/prepare`, {
          uplift_pct: pct, presentation_mode: mode,
        });
        id = r.data.id;
        setSpecId(id);
      } else {
        if (Math.abs(pct - savedUplift) > 0.0001) {
          await api.post(`/renewals/${id}/uplift`, { uplift_pct: pct });
        }
        if (mode !== savedMode) {
          await api.patch(`/renewals/${id}/presentation`, { presentation_mode: mode });
        }
      }
      const fresh = await api.get(`/renewals/${id}`);
      setProposal(fresh.data?.proposal || null);
      setSavedUplift(pct); setSavedMode(mode);
      await refreshPreview(pct, mode);
      toast.success("Draft saved. Nothing has been sent to the customer.");
      onPrepared?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the draft");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[88vh] overflow-y-auto"
                     data-testid="generate-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">
            {specId ? "Renewal proposal — draft" : "Generate Proposal"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#525252]">
            {specId
              ? "Editing the existing draft for the upcoming period. Changing the increase or the presentation never sends anything."
              : "Creates an internal draft renewal proposal from the current recurring specification."}
            {" "}Pulse does not raise invoices — FinOps does that later.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-xs text-[#A3A3A3]">Loading renewal…</div>
        ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-[#E5E5E5] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              Current annual recurring value
            </div>
            <div className="text-lg font-black mt-1" data-testid="proposal-current-arr">
              {money(internal?.prior_recurring_annual_value ?? base, currency)}
            </div>
          </div>
          <div className="border border-[#E5E5E5] p-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]"
                   htmlFor="uplift-pct">Annual increase %</label>
            <input id="uplift-pct" type="number" step="0.01" value={upliftText}
                   onChange={(e) => setUpliftText(e.target.value)}
                   data-testid="proposal-uplift-input"
                   className="w-full mt-1 border border-[#E5E5E5] px-2 py-1 text-lg font-black rounded-none" />
            <div className="text-[10px] text-[#A3A3A3] mt-1">
              Positive, zero, or negative where authorized. Typing changes nothing until you save.
            </div>
          </div>
          <div className="border-2 border-[#0055FF] bg-[#EFF6FF] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF]">
              Proposed new annual value
            </div>
            <div className="text-lg font-black text-[#0055FF] mt-1" data-testid="proposal-new-arr">
              {proposedVal == null ? "—" : money(proposedVal, currency)}
            </div>
            <div className="text-[10px] text-[#525252] mt-1" data-testid="proposal-increase">
              {increaseVal == null ? "Enter a percentage"
                : `Increase ${money(increaseVal, currency)}`}
            </div>
          </div>
        </div>

        <fieldset className="border border-[#E5E5E5] p-3">
          <legend className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] px-1">
            Proposal presentation
          </legend>
          {MODES.map(([value, label, help]) => (
            <label key={value} className="flex items-start gap-2 py-1 cursor-pointer">
              <input type="radio" name="presentation_mode" value={value}
                     checked={mode === value}
                     onChange={() => setMode(value)}
                     data-testid={`proposal-mode-${value}`}
                     className="mt-1" />
              <span>
                <span className="text-xs font-bold text-[#0A0A0A]">{label}</span>
                <span className="block text-[11px] text-[#525252]">{help}</span>
              </span>
            </label>
          ))}
          <p className="text-[11px] text-[#525252] mt-2 border-t border-[#F5F5F5] pt-2">
            Presentation only. The full line-level specification, licensed-user count
            and credits are always retained — next year&apos;s renewal still renews from
            line-level terms.
          </p>
        </fieldset>

        {preview ? (
          <div className="border border-[#E5E5E5]" data-testid="proposal-preview">
            <div className="bg-[#FAFAFA] px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
              <span className="text-xs font-bold">What the customer will see</span>
              <span className="text-[10px] uppercase tracking-wider text-[#525252]">
                {internal?.presentation_label} · {internal?.period_start} → {internal?.period_end}
              </span>
            </div>

            {cust?.mode === "detailed" ? (
              <table className="w-full text-xs" data-testid="proposal-preview-detailed">
                <tbody>
                  {(cust.lines || []).map((l, i) => (
                    <tr key={i} className="border-b border-[#F5F5F5]">
                      <td className="px-3 py-1.5">
                        {l.description}
                        {l.licensed_user_quantity
                          ? <span className="text-[#525252]"> — {l.licensed_user_quantity} licensed users</span>
                          : null}
                      </td>
                      <td className="px-3 py-1.5 text-right font-medium">
                        {money(l.annual_value, currency)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-[#FAFAFA] border-t border-[#0A0A0A]">
                    <td className="px-3 py-2 font-bold text-right uppercase tracking-wider text-[11px]">
                      Annual renewal fee
                    </td>
                    <td className="px-3 py-2 text-right font-black">
                      {money(cust.total_annual_value, currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : cust ? (
              <div className="p-3 text-xs" data-testid="proposal-preview-summary">
                <div className="font-bold text-sm">{cust.headline}</div>
                {cust.licensed_users ? (
                  <div className="text-[#525252]">{cust.licensed_users} licensed users</div>
                ) : null}
                {(cust.includes || []).length > 0 && (
                  <>
                    <div className="mt-2 text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                      Includes
                    </div>
                    <ul className="list-disc ml-5 text-[#525252]">
                      {cust.includes.map((inc, i) => <li key={i}>{inc}</li>)}
                    </ul>
                  </>
                )}
                <div className="mt-3 pt-2 border-t border-[#0A0A0A] flex items-center justify-between">
                  <span className="font-bold uppercase tracking-wider text-[11px]">
                    Annual renewal fee
                  </span>
                  <span className="font-black text-base">
                    {money(cust.annual_renewal_fee, currency)}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="bg-[#FAFAFA] px-3 py-2 border-t border-[#E5E5E5] text-[11px] text-[#525252]"
                 data-testid="proposal-internal-strip">
              <strong>Internal:</strong> prior {money(internal?.prior_recurring_annual_value, currency)}
              {" · "}uplift {Number(internal?.uplift_pct ?? 0).toFixed(2)}%
              {" · "}increase {money(internal?.uplift_amount, currency)}
              {" · "}proposed {money(internal?.proposed_recurring_annual_value, currency)}
              {" · "}{preview.underlying_line_count} underlying line
              {preview.underlying_line_count !== 1 ? "s" : ""} retained
              {internal?.is_unsaved_preview && (
                <span className="ml-2 font-bold text-[#B45309]">unsaved preview</span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-[#525252] border border-dashed border-[#E5E5E5] p-3">
            {specId ? "Adjust the increase to preview the proposal."
              : "Set the increase and presentation, then Create draft. The customer preview appears once the draft exists."}
          </p>
        )}

        {/* Wording is edited in the normal proposal editor — it is an
            ordinary Pulse proposal, so it already has title/notes/terms
            fields and we do not build a second editor beside them. */}
        {proposal?.quote_id && (
          <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-[11px] flex items-center justify-between gap-3"
               data-testid="proposal-wording-link">
            <span className="text-[#525252]">
              Customer-facing wording (title, introduction, notes, validity, terms) is
              edited on the proposal itself — <strong>{proposal.quote_number}</strong>,
              status {proposal.status}. Your wording is preserved when you change the
              increase or the presentation.
            </span>
            <Link to={`/quotes/${proposal.quote_id}`}
                  className="shrink-0 inline-flex items-center h-7 px-2 border border-[#E5E5E5] bg-white uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF]"
                  data-testid="open-linked-proposal">
              Open proposal
            </Link>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-none text-xs">Close</Button>
          <Button onClick={save} disabled={busy || !pctValid || (specId && !dirty)}
                  data-testid="proposal-save-draft"
                  className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs">
            {busy ? "Saving…" : specId ? "Save draft" : "Create draft"}
          </Button>
        </DialogFooter>

        <p className="text-[11px] text-[#525252]">
          Saving stores the draft only. Approving and sending are separate, explicit
          actions on the proposal — nothing reaches the customer, and nothing goes to
          FinOps, until you send it.
        </p>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
